<template>
  <aside class="platform-sidebar" :class="{ 'dark-theme': isDarkTheme }">
    <nav class="sidebar-nav">
      <!-- Dashboard -->
      <router-link to="/platform" class="nav-item" :class="{ active: isExactActive('/platform') }">
        <div class="nav-icon">
          <i class="pi pi-chart-pie"></i>
        </div>
        <span class="nav-label">Dashboard</span>
      </router-link>

      <!-- Tenants -->
      <router-link
        to="/platform/tenants"
        class="nav-item"
        :class="{ active: isActive('/platform/tenants') }"
      >
        <div class="nav-icon">
          <i class="pi pi-building"></i>
        </div>
        <span class="nav-label">Tenants</span>
        <span v-if="tenantCount" class="nav-badge">{{ tenantCount }}</span>
      </router-link>

      <!-- Platform Users (Admin only) -->
      <router-link
        v-if="isPlatformAdmin"
        to="/platform/users"
        class="nav-item"
        :class="{ active: isActive('/platform/users') }"
      >
        <div class="nav-icon">
          <i class="pi pi-users"></i>
        </div>
        <span class="nav-label">Platform Users</span>
      </router-link>

      <!-- Audit Logs -->
      <router-link
        to="/platform/audit"
        class="nav-item"
        :class="{ active: isActive('/platform/audit') }"
      >
        <div class="nav-icon">
          <i class="pi pi-history"></i>
        </div>
        <span class="nav-label">Audit Logs</span>
      </router-link>

      <!-- Divider -->
      <div v-if="isPlatformAdmin" class="nav-divider"></div>

      <!-- Settings (Admin only) -->
      <router-link
        v-if="isPlatformAdmin"
        to="/platform/settings"
        class="nav-item"
        :class="{ active: isActive('/platform/settings') }"
      >
        <div class="nav-icon">
          <i class="pi pi-cog"></i>
        </div>
        <span class="nav-label">Settings</span>
      </router-link>

      <!-- General Templates (Admin only) -->
      <router-link
        v-if="isPlatformAdmin"
        to="/platform/templates"
        class="nav-item"
        :class="{ active: isActive('/platform/templates') }"
      >
        <div class="nav-icon">
          <i class="pi pi-file-edit"></i>
        </div>
        <span class="nav-label">General Templates</span>
      </router-link>
    </nav>

    <!-- Quick Actions (Admin only) -->
    <div v-if="isPlatformAdmin" class="sidebar-footer">
      <button @click="showCreateTenant" class="create-tenant-btn">
        <i class="pi pi-plus"></i>
        New Tenant
      </button>
    </div>
  </aside>
</template>

<script>
import { mapGetters } from 'vuex';
import { platformApi } from '@/services/platformApi';

export default {
  name: 'PlatformSidebar',
  props: {
    isDarkTheme: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      tenantCount: 0,
    };
  },
  computed: {
    ...mapGetters('auth', ['getUser']),
    isPlatformAdmin() {
      // Check for platform_admin role (from JWT) or platformRole from store
      const user = this.getUser;
      const platformUser = JSON.parse(localStorage.getItem('platform_user') || '{}');
      return user?.platformRole === 'admin'
        || user?.platformRole === 'platform_admin'
        || platformUser?.role === 'platform_admin';
    },
  },
  mounted() {
    this.loadTenantCount();
  },
  methods: {
    isActive(path) {
      return this.$route.path.startsWith(path);
    },
    isExactActive(path) {
      return this.$route.path === path;
    },
    async loadTenantCount() {
      try {
        const stats = await platformApi.getStats();
        if (stats) {
          this.tenantCount = stats.totalTenants || 0;
        }
      } catch (error) {
        console.error('Failed to load tenant count:', error);
      }
    },
    showCreateTenant() {
      this.$router.push('/platform/tenants/new');
    },
  },
};
</script>

<style scoped>
/* Light Theme (Default) */
.platform-sidebar {
  --sidebar-bg: #ffffff;
  --sidebar-border: #e7e7e7;
  --nav-text: #666666;
  --nav-text-active: #1E2019;
  --nav-hover-bg: rgba(228, 130, 26, 0.08);

  width: 260px;
  background: var(--sidebar-bg);
  border-right: 1px solid var(--sidebar-border);
  display: flex;
  flex-direction: column;
  font-family: 'AsenPro', sans-serif;
  transition: all 0.3s ease;
}

/* Dark Theme */
.platform-sidebar.dark-theme {
  --sidebar-bg: #2a2a24;
  --sidebar-border: rgba(228, 130, 26, 0.2);
  --nav-text: #a8a8a0;
  --nav-text-active: #f5f5f5;
  --nav-hover-bg: rgba(228, 130, 26, 0.12);
}

.sidebar-nav {
  flex: 1;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-radius: 0.75rem;
  color: var(--nav-text);
  text-decoration: none;
  transition: all 0.2s;
  position: relative;
}

.nav-item:hover {
  background: var(--nav-hover-bg);
  color: #e4821a;
}

.nav-item.active {
  background: rgba(228, 130, 26, 0.1);
  color: var(--nav-text-active);
  border: 1px solid rgba(228, 130, 26, 0.2);
}

.nav-item.active::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 60%;
  background: linear-gradient(180deg, #c83a77, #e44b55, #e4821a);
  border-radius: 0 3px 3px 0;
}

.nav-icon {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(228, 130, 26, 0.1);
  border-radius: 0.5rem;
  font-size: 1rem;
  color: #e4821a;
}

.nav-item.active .nav-icon {
  background: linear-gradient(135deg, #c83a77, #e44b55, #e4821a);
  color: white;
}

.nav-label {
  font-size: 0.9rem;
  font-weight: 500;
}

.nav-badge {
  margin-left: auto;
  background: linear-gradient(135deg, #c83a77, #e44b55, #e4821a);
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.125rem 0.5rem;
  border-radius: 9999px;
  min-width: 1.5rem;
  text-align: center;
}

.nav-divider {
  height: 1px;
  background: var(--sidebar-border);
  margin: 1rem 0;
}

.sidebar-footer {
  padding: 1rem;
  border-top: 1px solid var(--sidebar-border);
}

.create-tenant-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: linear-gradient(135deg, #c83a77, #e44b55, #e4821a);
  border: none;
  border-radius: 2rem;
  color: white;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.create-tenant-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 30px rgba(228, 130, 26, 0.3);
}
</style>
